import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import YouTubeIcon from '@mui/icons-material/YouTube';

// Swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export default function HeroSection() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const heroHeight = isSm ? '60vh' : '78vh';

  let imageSrc;
  try {
    imageSrc = require(`../../assets/images/home/hero1.png`);
  } catch (err) {
    imageSrc = 'hero1.png';
  }

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        width: '100%',
        height: heroHeight,
        mt: { xs: '12px', md: '17px' },
      }}
    >
      <Swiper
        modules={[Navigation, Pagination, Autoplay, A11y]}
        slidesPerView={1}
        loop
        navigation
        pagination={{ clickable: true }}
        autoplay={{ delay: 6500, disableOnInteraction: false }}
        style={{ height: '100%' }}
        aria-label="Hero slider"
      >
        {/* SLIDE 1: Platform */}
        <SwiperSlide>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              backgroundImage: `url(${imageSrc})`,
              backgroundSize: 'cover',
              backgroundPosition: { xs: 'right', md: 'center' },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Container
              maxWidth="lg"
              sx={{
                position: 'relative',
                zIndex: 2,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                pl: { xs: 3, md: 0 },
              }}
            >
              <Box
                sx={{
                  maxWidth: { xs: '92%', md: '56%' },
                  textAlign: 'left',
                  color: { xs: '#081426', md: '#fff' },
                  mx: 0,
                }}
              >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    display: 'inline-block',
                    bgcolor: 'rgba(255, 255, 255, 0.45)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    color: 'rgba(5, 4, 4, 1)',
                    fontWeight: 900,
                    mb: 0,
                  }}
                >
                  Platform
                </Typography>

                <Box
                  component="a"
                  href="https://www.youtube.com/@skillserve23"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    bgcolor: 'rgba(255, 0, 0, 0.08)',
                    px: 1.8,
                    py: 0.5,
                    borderRadius: 20,
                    textDecoration: 'none',
                    border: '1px solid rgba(255, 0, 0, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 0, 0, 0.12)',
                      borderColor: 'rgba(255, 0, 0, 0.4)',
                    }
                  }}
                >
                  <YouTubeIcon sx={{ color: '#ff0000', fontSize: 18, fontWeight: 700 }} />
                  <Typography sx={{ color: '#ff0000', fontWeight: 700, fontSize: '0.8rem' }}>
                    Watch Demo
                  </Typography>
                </Box>
              </Box>

                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: '1.5rem', md: '2.8rem' },
                    fontWeight: 800,
                    lineHeight: 1.03,
                    mb: 1.5,
                    color: 'rgba(42, 7, 240, 0.92)',
                  }}
                >
                  Build A Skilled Workforce With SkillServe
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    color: { xs: '#081426', md: 'rgba(10, 10, 10, 0.92)' },
                    mb: 1,
                    display: { xs: 'none', md: 'block' },
                  }}
                >
                  A modern Learning Management System designed to deliver structured learning, workforce training, and industry-aligned skill development programs.
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    color: { xs: '#081426', md: 'rgba(10, 10, 10, 0.8)' },
                    mb: 3,
                    display: { xs: 'none', md: 'block' },
                  }}
                >
                  SkillServe enables organizations, training partners, and professionals to access practical learning through expert-designed courses and a powerful digital learning platform.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-start' }, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/allCourses/all')}
                    sx={{
                      background: 'linear-gradient(90deg,#fdd649,#f7c42d)',
                      color: '#081426',
                      px: 3,
                      py: 1.25,
                      fontWeight: 700,
                      borderRadius: 2,
                      boxShadow: '0 10px 30px rgba(250,186,40,0.12)',
                      '&:hover': { transform: 'translateY(-2px)' },
                    }}
                    aria-label="Explore Courses"
                  >
                    Explore Courses
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => navigate('/corporate')}
                    sx={{
                      color: { xs: '#081426', md: '#fff' },
                      borderColor: 'rgba(255,255,255,0.28)',
                      px: 2,
                      py: 1.1,
                      borderRadius: 2,
                      borderWidth: 1,
                    }}
                  >
                    For Organizations
                  </Button>
                </Box>
              </Box>
            </Container>
          </Box>
        </SwiperSlide>

        {/* SLIDE 2: Credibility */}
        <SwiperSlide>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              backgroundImage: `url(${imageSrc})`,
              backgroundSize: 'cover',
              backgroundPosition: { xs: 'right', md: 'center' },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Container
              maxWidth="lg"
              sx={{
                position: 'relative',
                zIndex: 2,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                pl: { xs: 3, md: 0 },
              }}
            >
              <Box
                sx={{
                  maxWidth: { xs: '92%', md: '56%' },
                  textAlign: 'left',
                  color: { xs: '#081426', md: '#fff' },
                  mx: 0,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    display: 'inline-block',
                    bgcolor: 'rgba(255, 255, 255, 0.45)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    color: 'rgba(5, 4, 4, 1)',
                    fontWeight: 900,
                    mb: 2,
                  }}
                >
                  Credibility
                </Typography>

                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: '1.5rem', md: '2.8rem' },
                    fontWeight: 800,
                    lineHeight: 1.03,
                    mb: 1.5,
                    color: 'rgba(42, 7, 240, 0.92)',
                  }}
                >
                  Trusted Learning Platform
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    color: { xs: '#081426', md: 'rgba(10, 10, 10, 0.92)' },
                    mb: 1,
                    display: { xs: 'none', md: 'block' },
                  }}
                >
                  SkillServe brings together industry expertise, structured learning programs, and modern digital delivery to support skill development across sectors.
                </Typography>

                <Box component="ul" sx={{ pl: 2, mb: 3, mt: 0, color: { xs: '#081426', md: 'rgba(10, 10, 10, 0.92)' }, display: { xs: 'none', md: 'block' } }}>
                  {["Industry Expert Led Programs", "Practical Skill Development Courses", "Digital Learning Through LMS Platform", "Workforce Training Solutions"].map((highlight, i) => (
                    <Typography component="li" key={i} sx={{ fontSize: { xs: '0.9rem', md: '1.05rem' }, mb: 0.5, fontWeight: 500 }}>
                      {highlight}
                    </Typography>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-start' }, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/allCourses/all')}
                    sx={{
                      background: 'linear-gradient(90deg,#fdd649,#f7c42d)',
                      color: '#081426',
                      px: 3,
                      py: 1.25,
                      fontWeight: 700,
                      borderRadius: 2,
                      boxShadow: '0 10px 30px rgba(250,186,40,0.12)',
                      '&:hover': { transform: 'translateY(-2px)' },
                    }}
                    aria-label="Explore Courses"
                  >
                    Explore Courses
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => navigate('/corporate')}
                    sx={{
                      color: { xs: '#081426', md: '#fff' },
                      borderColor: 'rgba(255,255,255,0.28)',
                      px: 2,
                      py: 1.1,
                      borderRadius: 2,
                      borderWidth: 1,
                    }}
                  >
                    For Organizations
                  </Button>
                </Box>
              </Box>
            </Container>
          </Box>
        </SwiperSlide>
      </Swiper>
    </Box>
  );
}